// Client-side aggregation for the Reporting "Contacts per hour" trend charts.
// The server (getContactsPerHour / getContactsPerHourByIntent) always returns
// the finest grain -- one point per agent/intent per day, { count, activeHours
// } -- so switching between Daily/Weekly/Monthly views is instant (no
// refetch): this just re-buckets and re-sums whatever the last response
// already contains.
//
// Weekly/monthly values are safe to compute by SUMMING each day's already
// interval-merged activeHours, without re-running the merge across day
// boundaries -- a day's activeHours was merged from that day's own spans, and
// two different days' spans essentially never overlap in wall-clock time
// (each whole span is attributed to a single day, see the server function's
// module comment), so summing pre-merged daily totals is equivalent to
// merging across the whole bucket.
//
// A day/week/month with NO covered occurrences for a given entity has no key
// in its `points` at all -- callers (LineChart) render that as a gap in the
// line, not a dip to zero, since "no shift"/"no volume" isn't "zero
// efficiency."
//
// Entity-agnostic: works identically whether each item in `series` represents
// an agent (getContactsPerHour) or an intent (getContactsPerHourByIntent) --
// it only ever looks at `.key` and `.points`.

function ymd(date) {
  return date.toISOString().slice(0, 10)
}

// Sunday of the week containing dateStr -- same Sun-Sat convention as the
// server's "Last week"/"This week" presets (routes/admin-reporting.js).
export function weekStartOf(dateStr) {
  const d = new Date(Date.parse(`${dateStr}T00:00:00Z`))
  const dow = d.getUTCDay() // 0=Sun..6=Sat
  return ymd(new Date(d.getTime() - dow * 86400000))
}

// The 1st of the calendar month containing dateStr.
export function monthStartOf(dateStr) {
  const d = new Date(Date.parse(`${dateStr}T00:00:00Z`))
  return ymd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)))
}

function bucketOf(dateStr, granularity) {
  if (granularity === 'week') return weekStartOf(dateStr)
  if (granularity === 'month') return monthStartOf(dateStr)
  return dateStr
}

// Contacts per hour: throughput. Used for the per-agent chart, where two
// overlapping claims by the same agent are already merged server-side into
// one activeHours span (one person, one clock).
function contactsPerHourRate({ count, activeHours }) {
  return activeHours > 0 ? count / activeHours : 0
}

// AHT: minutes per contact, the reciprocal framing of the same { count,
// activeHours } numbers. Used for the by-intent chart -- see
// getContactsPerHourByIntent's module comment on why activeHours there is
// summed labor-time, not a merged wall-clock span, which is exactly what AHT
// wants (total time spent on this intent, divided by how many contacts).
function ahtMinutes({ count, activeHours }) {
  return count > 0 ? (activeHours * 60) / count : 0
}

// dates: ['YYYY-MM-DD', ...] the full requested range, in order.
// series: [{ key, points: { 'YYYY-MM-DD': { count, activeHours } } }].
// granularity: 'day' | 'week' | 'month'.
// valueFn: ({ count, activeHours }) => number -- the metric to compute per
// bucket, applied AFTER summing count/activeHours across the bucket's days.
// Returns { bucketDates, series: [{ key, label, points: { bucketDate: value } }] }
// -- points only has keys for buckets with at least one covered occurrence;
// everything else is a deliberate gap.
function bucketAndAggregate({ dates, series, granularity, valueFn }) {
  if (granularity === 'day') {
    return {
      bucketDates: dates,
      series: series.map((s) => ({
        key: s.key,
        label: s.key,
        points: Object.fromEntries(
          dates.filter((d) => s.points[d]).map((d) => [d, valueFn(s.points[d])]),
        ),
      })),
    }
  }

  const bucketDatesSet = new Set(dates.map((d) => bucketOf(d, granularity)))
  const bucketDates = [...bucketDatesSet].sort()

  const aggregated = series.map((s) => {
    const sums = {}
    for (const d of dates) {
      const p = s.points[d]
      if (!p) continue
      const b = bucketOf(d, granularity)
      sums[b] ??= { count: 0, activeHours: 0 }
      sums[b].count += p.count
      sums[b].activeHours += p.activeHours
    }
    const points = Object.fromEntries(Object.entries(sums).map(([b, v]) => [b, valueFn(v)]))
    return { key: s.key, label: s.key, points }
  })

  return { bucketDates, series: aggregated }
}

// Per-agent "Contacts per hour" chart -- throughput (contacts/hr).
export function aggregateContactsPerHour({ dates, series, granularity }) {
  return bucketAndAggregate({ dates, series, granularity, valueFn: contactsPerHourRate })
}

// Per-intent "AHT by intent" trend chart -- average handle time, in minutes.
export function aggregateAhtByIntent({ dates, series, granularity }) {
  return bucketAndAggregate({ dates, series, granularity, valueFn: ahtMinutes })
}
